<template>
  <page>
    <lazy-div :load="doInit">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Team</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Team</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">View Existing Team</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container grid-list-md>
            <v-layout row wrap>
              <v-flex xs6 v-if="newMode">
                <v-text-field
                  v-model="form.team"
                  :label="label.team"
                  :error-messages="errors.collect('team')"
                  data-vv-name="team"
                  :data-vv-as="label.team"
                  v-validate="'required|max:10'"
                ></v-text-field>
              </v-flex>
              <!-- Review -->
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ label.team }}</v-subheader>
                <div class="queryValue">{{ form.team }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="form.teamDesc"
                  :label="label.teamDesc"
                  :error-messages="errors.collect('teamDesc')"
                  data-vv-name="teamDesc"
                  :data-vv-as="label.teamDesc"
                  v-validate="'required|max:30'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ label.teamDesc }}</v-subheader>
                <div class="queryValue">{{ form.teamDesc }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex xs6 v-if="newMode">
                <v-autocomplete
                  :items="divisionList"
                  v-model="form.division"
                  :label="label.division"
                  item-text="label"
                  item-value="value"
                  :error-messages="errors.collect('division')"
                  data-vv-name="division"
                  :data-vv-as="label.division"
                  v-validate="'required'"
                  @input="doGetSecByDiv($event)"
                  clearable
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ label.division }}</v-subheader>
                <div class="queryValue" v-for="item in filterDivisionList" :key="item.label">{{ item.label }}</div>
              </v-flex>
              <v-flex xs6 v-if="newMode">
                <v-autocomplete
                  :items="sectorDivList"
                  v-model="form.sector"
                  :label="label.sector"
                  item-text="label"
                  item-value="value"
                  :error-messages="errors.collect('sector')"
                  data-vv-name="sector"
                  :data-vv-as="label.sector"
                  v-validate="'required'"
                  clearable
                ></v-autocomplete>
              </v-flex>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ label.sector }}</v-subheader>
                <div class="queryValue" v-for="item in filterSectorList" :key="item.label">{{ item.label }}</div>
              </v-flex>
            </v-layout>

            <v-layout wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ label.createdBy }}</v-subheader>
                <div class="queryValue">{{ form.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ label.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(form.createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout wrap v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ label.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ form.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ label.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(form.modifiedDate) }}</div>
              </v-flex>
            </v-layout>
          </v-container>
          <v-spacer class="mt-3"></v-spacer>
          <v-layout>
            <v-flex xs6>
              <v-btn color="red darken-1" dark class=" mr-2" @click.native="doCancel">
                Cancel
                <v-icon right dark>mdi-cancel</v-icon>
              </v-btn>
              <v-btn v-if="!queryMode" color="primary" dark class=" mr-2" @click.prevent="doSave" :loading="submitting">
                Save
                <v-icon right>mdi-content-save</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import { commonMixins } from '@/mixins/common/commonMixin'
import { formatDisplayDate } from '@/utils/date'
import { expandURI } from '@/utils/ajax'

export default {
  data() {
    return {
      submitting: false,
      newMode: false,
      reviewMode: false,
      queryMode: false,
      divisionList: [],
      sectorDivList: [],
      label: {
        team: 'Team',
        division: 'Division',
        sector: 'Sector',
        teamDesc: 'Team Description',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      form: {
        team: null,
        division: null,
        sector: null,
        teamDesc: null,
        createdBy: null,
        createdDate: null,
        modifiedBy: null,
        modifiedDate: null
      },
      today: new Date(),
      loading: false
    }
  },
  methods: {
    async doInit() {
      await this.$store.dispatch('teamInit')
      if (this.newTranMode) {
        this.form.createdBy = this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName
        this.form.createdDate = new Date()
      } else {
        this.fetchData(true)
      }
      this.fetchDivisionList()
      this.fetchSectorList()
    },
    async fetchData() {
      const apiURI = this.menu.getTeam
      if (apiURI) {
        try {
          const expand = {
            team: this.teamId.team,
            division: this.teamId.division,
            sector: this.teamId.sector
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          const { data } = result.data
          if (data) {
            this.form.team = data.team.team
            this.form.division = data.team.division
            this.form.sector = data.team.sector
            this.form.teamDesc = data.team.teamDesc
            this.form.createdBy = data.createdBy
            this.form.createdDate = data.createdDate
            this.form.modifiedBy = data.modifiedBy
            this.form.modifiedDate = data.modifiedDate
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doSave() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newTranMode) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.createTeam
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'team' })
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdate() {
      const apiURI = this.menu.updateTeam
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'team' })
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async fetchDivisionList() {
      const apiUrl = this.menu.divisionList
      if (apiUrl) {
        try {
          this.divisionList = []
          const result = await this.$http.get(apiUrl)
          if (result.data.dataList) {
            this.$set(this, 'divisionList', result.data.dataList)
          }
        } catch (err) {
          this.divisionList = []
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchSectorList() {
      const apiUrl = this.menu.sectorList
      if (apiUrl) {
        try {
          this.sectorDivList = []
          const result = await this.$http.get(apiUrl)
          if (result.data.dataList) {
            this.$set(this, 'sectorDivList', result.data.dataList)
          }
        } catch (err) {
          this.sectorDivList = []
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doCancel() {
      this.$router.push({ name: 'team' })
    },
    formatDisplayDate(xDate) {
      if (xDate === null) {
        return undefined
      } else {
        return formatDisplayDate(xDate)
      }
    },
    doGetSecByDiv(event) {
      if (this.form.division !== '') {
        this.loading = true
        this.sectorDivList = []
        this.$validator.validate('division').then((result) => {
          if (result) {
            const apiURI = this.menu.getSectorListByDivision
            if (apiURI) {
              const expand = {
                division: this.form.division
              }
              this.$http
                .get(expandURI(apiURI, expand))
                .then((result) => {
                  const { data } = result.data
                  if (data) {
                    data.filter((list) => {
                      this.sectorDivList.push({ value: list.value, label: list.label })
                    })
                  } else {
                    this.loading = true
                    this.sectorDivList = []
                  }
                })
                .catch((err) => {
                  this.sectorDivList = []
                  const { data } = err.response
                  if (data) {
                    // this.message(data)
                    this.fieldMessage(data)
                    this.notify(data)
                  }
                })
            }
          } else {
            this.form.sector = null
            this.fetchSectorList()
          }
        })
      }
    }
  },
  created() {
    const jsonData = JSON.parse(sessionStorage.getItem('team'))
    this.$store.commit('TEAM_COMMIT_KEY', jsonData)

    if (this.teamId.tranMode !== '') {
      if (this.teamId.tranMode === 'New') {
        this.newMode = true
      } else if (this.teamId.tranMode === 'Review') {
        this.reviewMode = true
      } else if (this.teamId.tranMode === 'Query') {
        this.queryMode = true
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.boIncenSchParam.team.menu
    },
    newTranMode() {
      return this.teamId.tranMode === 'New'
    },
    teamId() {
      return this.$store.state.boIncenSchParam.team.teamId
    },
    filterDivisionList() {
      return this.divisionList.filter((list) => {
        return list.value === this.form.division
      })
    },
    filterSectorList() {
      return this.sectorDivList.filter((list) => {
        return list.value === this.form.sector
      })
    }
  },
  mixins: [MessageMixin, commonAjaxMixin, commonMixins]
}
</script>
